import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ActivateIcon,
  ToggleOff as DeactivateIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as TreeIcon,
  CheckCircle as FinalIcon,
  RadioButtonUnchecked as NonFinalIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useApi } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';

const TypificationTreeBuilder = ({ group, onTreeUpdate }) => {
  const { ends } = useApi();
  const { showSuccess, showError } = useNotification();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Dialog states
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [parentNode, setParentNode] = useState(null);
  const [editingHasChildren, setEditingHasChildren] = useState(false);
  const [nodeForm, setNodeForm] = useState({
    name: '',
    code: '',
    is_final: false
  });

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuNode, setMenuNode] = useState(null);

  // Load tree data
  const loadTree = async () => {
    try {
      setLoading(true);
      const response = await ends.getTree(group.id, { include_inactive: true });
      if (response.success) {
        setTree(response.data || []);
        // Auto-expand first level
        const firstLevelIds = (response.data || []).map(node => node.id);
        setExpandedNodes(new Set(firstLevelIds));
      } else {
        showError('Error al cargar tipificaciones');
      }
    } catch (error) {
      console.error('Error loading tree:', error);
      showError('Error al cargar tipificaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (group) {
      loadTree();
    }
  }, [group]);

  // Helper function to count levels in tree
  const getMaxCurrentLevel = (nodes, level = 1) => {
    let maxLevel = level;
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        maxLevel = Math.max(maxLevel, getMaxCurrentLevel(node.children, level + 1));
      }
    }
    return maxLevel;
  };

  // Node management functions
  const handleCreateNode = (parent = null) => {
    const newLevel = parent ? parent.level + 1 : 1;

    if (newLevel > group.max_levels) {
      showError(`No se pueden crear más niveles. Máximo permitido: ${group.max_levels}`);
      return;
    }

    setEditingNode(null);
    setParentNode(parent);
    setEditingHasChildren(false);
    setNodeForm({
      name: '',
      code: '',
      is_final: newLevel === group.max_levels // Auto-mark as final if it's the last level
    });
    setNodeDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleEditNode = (node) => {
    setEditingNode(node);
    setParentNode(null);
    const hasChildren = node.children && node.children.length > 0;
    setEditingHasChildren(hasChildren);
    setNodeForm({
      name: node.name,
      code: node.code || '',
      is_final: hasChildren ? false : node.is_final
    });
    setNodeDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleSaveNode = async () => {
    try {
      if (!nodeForm.name.trim()) {
        showError('El nombre es requerido');
        return;
      }

      let response;
      if (editingNode) {
        response = await ends.updateTypification(editingNode.id, nodeForm);
      } else {
        const level = parentNode ? parentNode.level + 1 : 1;
        response = await ends.createTypification(group.id, {
          ...nodeForm,
          level,
          parent_id: parentNode?.id || null
        });
      }

      if (response.success) {
        showSuccess(editingNode ? 'Tipificación actualizada' : 'Tipificación creada');
        setNodeDialogOpen(false);
        loadTree();
        onTreeUpdate?.();
      } else {
        showError(response.error || 'Error al guardar tipificación');
      }
    } catch (error) {
      console.error('Error saving node:', error);
      showError('Error al guardar tipificación');
    }
  };

  const handleDeleteNode = async (node) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${node.name}" y todos sus hijos?`)) {
      return;
    }

    try {
      const response = await ends.deleteTypification(node.id);
      if (response.success) {
        showSuccess('Tipificación eliminada');
        loadTree();
        onTreeUpdate?.();
      } else {
        showError(response.error || 'Error al eliminar tipificación');
      }
    } catch (error) {
      console.error('Error deleting node:', error);
      showError('Error al eliminar tipificación');
    }
    setMenuAnchor(null);
  };

  const handleToggleActive = async (node, nextActive) => {
    try {
      const response = await ends.updateTypification(node.id, { is_active: nextActive });
      if (response.success) {
        showSuccess(nextActive ? 'Tipificación activada' : 'Tipificación desactivada');
        loadTree();
        onTreeUpdate?.();
      } else {
        showError(response.error || 'Error al actualizar tipificación');
      }
    } catch (error) {
      console.error('Error updating node status:', error);
      showError('Error al actualizar tipificación');
    }
    setMenuAnchor(null);
  };

  const handleToggleExpand = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleMenuOpen = (event, node) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuNode(node);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuNode(null);
  };

  // Render tree node
  const renderTreeNode = (node, level = 1) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const canAddChild = level < group.max_levels;
    const isActive = node.is_active !== false;

    return (
      <Box key={node.id} sx={{ mb: 1 }}>
        <Card
          variant="outlined"
          sx={{
            ml: (level - 1) * 3,
            backgroundColor: !isActive
              ? 'grey.100'
              : level === 1
                ? 'primary.50'
                : level === 2
                  ? 'secondary.50'
                  : 'grey.50',
            opacity: isActive ? 1 : 0.7,
            borderColor: isActive ? 'divider' : 'grey.300'
          }}
        >
          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasChildren && (
                  <IconButton
                    size="small"
                    onClick={() => handleToggleExpand(node.id)}
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s'
                      }}
                    />
                  </IconButton>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {node.is_final ? (
                    <FinalIcon color={isActive ? 'success' : 'disabled'} />
                  ) : (
                    <NonFinalIcon color={isActive ? 'action' : 'disabled'} />
                  )}

                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={level === 1 ? 600 : 400}
                      color={isActive ? 'text.primary' : 'text.disabled'}
                    >
                      {node.name}
                    </Typography>
                    {node.code && (
                      <Typography variant="caption" color={isActive ? 'text.secondary' : 'text.disabled'}>
                        Código: {node.code}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Chip
                  label={`Nivel ${level}`}
                  size="small"
                  color={level === 1 ? 'primary' : level === 2 ? 'secondary' : 'default'}
                  variant="outlined"
                />
                {!isActive && (
                  <Chip
                    label="Inactivo"
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>

              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, node)}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Render children */}
        {hasChildren && isExpanded && (
          <Box sx={{ mt: 1 }}>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return <Typography>Cargando árbol de tipificaciones...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Constructor de Tipificaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grupo: {group.name} | Niveles máximos: {group.max_levels}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleCreateNode()}
        >
          Agregar Nivel 1
        </Button>
      </Box>

      {tree.length === 0 ? (
        <Alert severity="info">
          No hay tipificaciones creadas. Comienza agregando el primer nivel.
        </Alert>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Estructura del Árbol ({tree.length} nodos principales)
          </Typography>
          {tree.map(node => renderTreeNode(node))}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuNode && menuNode.level < group.max_levels && (
          <MenuItem onClick={() => handleCreateNode(menuNode)}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Agregar Hijo</ListItemText>
          </MenuItem>
        )}
        {menuNode && (
          <MenuItem onClick={() => handleToggleActive(menuNode, menuNode.is_active === false)}>
            <ListItemIcon>
              {menuNode.is_active === false ? (
                <ActivateIcon fontSize="small" />
              ) : (
                <DeactivateIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{menuNode.is_active === false ? 'Activar' : 'Desactivar'}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleEditNode(menuNode)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteNode(menuNode)} sx={{ color: 'secondary.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Node Dialog */}
      <Dialog open={nodeDialogOpen} onClose={() => setNodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNode ? 'Editar Tipificación' : 'Nueva Tipificación'}
          {parentNode && ` - Hijo de "${parentNode.name}"`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre *"
            fullWidth
            variant="outlined"
            value={nodeForm.name}
            onChange={(e) => setNodeForm({ ...nodeForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Código (opcional)"
            fullWidth
            variant="outlined"
            value={nodeForm.code}
            onChange={(e) => setNodeForm({ ...nodeForm, code: e.target.value })}
            sx={{ mb: 2 }}
            helperText="Código único para identificación automática"
          />
          <FormControlLabel
            control={
              <Switch
                checked={nodeForm.is_final}
                onChange={(e) => setNodeForm({ ...nodeForm, is_final: e.target.checked })}
                color="primary"
                disabled={editingHasChildren}
              />
            }
            label="Es tipificación final (hoja del árbol)"
          />
          {editingHasChildren && (
            <Typography variant="caption" color="text.secondary">
              No se puede marcar como final porque tiene tipificaciones hijas.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveNode} variant="contained">
            {editingNode ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TypificationTreeBuilder;
